import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
} from "lexical";
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
} from "@lexical/selection";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { $isListNode, ListNode } from "@lexical/list";
import { $isHeadingNode } from "@lexical/rich-text";
import styles from "./ToolbarPlugin.module.scss";
import classNames from "classnames";
import { BlockType, LowPriority, ToolBarOptions } from "../../config/constants";
import { BlockOptionsDropdownList } from "./BlockOptionsDropdownList/BlockOptionsDropdownList";
import { ReactComponent as BoldIcon } from "../../../../assets/icons/bold.svg";
import { ReactComponent as ItalicIcon } from "../../../../assets/icons/italic.svg";
import { ReactComponent as UnderlineIcon } from "../../../../assets/icons/underline.svg";
import { ReactComponent as AlignedLeftIcon } from "../../../../assets/icons/aligned_left.svg";
import { ReactComponent as AlignedRightIcon } from "../../../../assets/icons/aligned_right.svg";
import { ReactComponent as AlignedCenterIcon } from "../../../../assets/icons/aligned_center.svg";
import { ReactComponent as UnorderedIcon } from "../../../../assets/icons/unordered_list.svg";
import { ReactComponent as OrderedIcon } from "../../../../assets/icons/ordered_list.svg";
import DropdownColorPicker from "./DropdownColorPicker/DropdownColorPicker";
import { FontSizeDropDown } from "./FontSizeDropDown/FontSizeDropDown";

interface ToolbarPluginProps {
  header?: React.ReactNode;
  headerClassName?: string;
  toolbarOptions?: Partial<typeof ToolBarOptions>;
  toolbarBorder?: boolean;
  headerPosition?: "left" | "bottom" | "top" | "right";
}

export default function ToolbarPlugin({
  header,
  headerClassName,
  toolbarBorder = true,
  headerPosition = "left",
  toolbarOptions: toolbarOptionsProps = ToolBarOptions,
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [blockType, setBlockType] = useState<BlockType>("paragraph");
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const [fontSize, setFontSize] = useState<string>("14px");
  const [fontColor, setFontColor] = useState<string>("black");

  const toolbarOptions = { ...ToolBarOptions, ...toolbarOptionsProps };

  const applyStyleText = useCallback(
    (styles: Record<string, string>) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if (selection === null) {
          return;
        }

        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [activeEditor]
  );

  const onFontColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ color: value });
    },
    [applyStyleText]
  );

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (selection === null) {
      return;
    }
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type as typeof blockType);
        }
      }
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));

      // Handle buttons
      setFontSize(
        $getSelectionStyleValueForProperty(selection, "font-size", "14px")
      );
      setFontColor(
        $getSelectionStyleValueForProperty(selection, "color", "#000")
      );
    }
  }, [activeEditor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      LowPriority
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      })
    );
  }, [updateToolbar, activeEditor, editor]);

  return (
    <div
      className={classNames(styles["ground"], {
        [styles["toolbar-border"]]: toolbarBorder,
        [styles["header-position-left"]]: headerPosition === "left",
        [styles["header-position-bottom"]]: headerPosition === "bottom",
        [styles["header-position-top"]]: headerPosition === "top",
        [styles["header-position-right"]]: headerPosition === "right",
      })}
    >
      {header ? (
        <div className={classNames(headerClassName, styles["header"])}>
          {header}
        </div>
      ) : null}
      <div className={styles["toolbar"]} ref={toolbarRef}>
        {toolbarOptions["blockType"] ? (
          <BlockOptionsDropdownList
            disabled={!isEditable}
            editor={editor}
            blockType={blockType}
          />
        ) : null}
        {toolbarOptions["fontSize"] ? (
          <FontSizeDropDown
            disabled={!isEditable}
            value={fontSize}
            editor={editor}
          />
        ) : null}
        {toolbarOptions["bold"] ||
        toolbarOptions["italic"] ||
        toolbarOptions["underline"] ||
        toolbarOptions["fontColor"] ? (
          <Divider />
        ) : null}

        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["active"]]: isBold,
            [styles["hidden"]]: !toolbarOptions["bold"],
          })}
          aria-label="Format Bold"
          disabled={!isEditable}
        >
          <BoldIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["active"]]: isItalic,
            [styles["hidden"]]: !toolbarOptions["italic"],
          })}
          aria-label="Format Italics"
          disabled={!isEditable}
        >
          <ItalicIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["active"]]: isUnderline,
            [styles["hidden"]]: !toolbarOptions["underline"],
          })}
          aria-label="Format Underline"
          disabled={!isEditable}
        >
          <UnderlineIcon />
        </button>
        {toolbarOptions["fontColor"] ? (
          <DropdownColorPicker
            disabled={!isEditable}
            color={fontColor}
            onChange={onFontColorSelect}
          />
        ) : null}
        {toolbarOptions["alignLeft"] ||
        toolbarOptions["alignCenter"] ||
        toolbarOptions["alignRight"] ||
        toolbarOptions["orderedList"] ||
        toolbarOptions["unorderedList"] ? (
          <Divider />
        ) : null}
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["hidden"]]: !toolbarOptions["alignLeft"],
          })}
          aria-label="Left Align"
          disabled={!isEditable}
        >
          <AlignedLeftIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["hidden"]]: !toolbarOptions["alignCenter"],
          })}
          aria-label="Center Align"
          disabled={!isEditable}
        >
          <AlignedCenterIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["hidden"]]: !toolbarOptions["alignRight"],
          })}
          aria-label="Right Align"
          disabled={!isEditable}
        >
          <AlignedRightIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            if (blockType !== "ul") {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            } else {
              editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
            }
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["active"]]: blockType === "ul",
            [styles["hidden"]]: !toolbarOptions["unorderedList"],
          })}
          aria-label="Bullet List"
          disabled={!isEditable}
        >
          <UnorderedIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            if (blockType !== "ol") {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            } else {
              editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
            }
          }}
          className={classNames(styles["toolbar-item"], styles["spaced"], {
            [styles["active"]]: blockType === "ol",
            [styles["hidden"]]: !toolbarOptions["orderedList"],
          })}
          aria-label="Numbered List"
          disabled={!isEditable}
        >
          <OrderedIcon />
        </button>
      </div>
    </div>
  );
}

function Divider() {
  return <div className={styles["divider"]} />;
}
